import { GRPCFunction } from '@fullstory/requests';
import {
  RequestAction,
  RequestLifecycleAction,
  RequestActionType,
  AbortType,
  CancelPendingType,
  RequestQueueAction,
  RequestActionQueueType,
  GRPCRequestAction,
  GRPCBodyType,
  GRPCResponseType,
  RequestCancellationAction,
  RequestCancellationActionType,
} from '../../types';
import { createAction } from '../../utils/types';
import { UID } from '@fullstory/util/uid';

/**
 * The parallel to createRequestAction, but for gRPC endpoints. These are required to be used with
 * functions that match the GRPCFunction signature, so all the request/response types can be inferred from
 * the contents of that function. See the helper types for more details.
 *
 * @param request - contains all of the information required to send a gRPC request, see GRPCRequestAction for more details
 * @param successType - a string literal action type for the action fired on success
 * @param failureType - a string literal action type for the action fired on failure
 * @param config.modify (optional) - a sync transformation function from D -> M, used
 * to modify the request before the action is dispatched.
 * @param config.cacheKey (optional) - This function returns the string used as the
 * key in the cache. All requests going through createRequestAction are cached based
 * on a default combination of the url and a hash of the payload body (see sendRequestAction).
 * However, there are times when a customized cache key may be needed to control the
 * number of entries going into the cache.
 * @param config.uid (optional) - If included, this will be the UID that can be used
 * to cancel this individual request. See createRequestCancellationAction.
 *
 * @generic RequestType: The action type that's dispatched with the *started* event.
 * @generic SuccessType: The action type that's dispatched with the *successful* request event.
 * @generic FailureType: The action type that's dispatched with the *failed* request event.
 * @generic BodyType: The type that is passed as the request body.
 * @generic ResponseType: The type that is returned from the gRPC endpoint.
 * @generic ModifyType: If the modify param is passed, this is the shape of the modified data that's dispatched with the success action.
 */
export function createGrpcAction<
  RequestType extends string,
  SuccessType extends string,
  FailureType extends string,
  BodyType,
  ResponseType,
>(
  request: GRPCRequestAction<RequestType, BodyType, ResponseType>,
  successType: SuccessType,
  failureType: FailureType,
  config?: {
    cacheKey?: (url: string) => string;
    uid?: UID;
  },
): RequestLifecycleAction<RequestType, SuccessType, FailureType, BodyType, ResponseType>;

// Overload #2: Complete customization: modified data and an optional customized cache key
export function createGrpcAction<
  RequestType extends string,
  SuccessType extends string,
  FailureType extends string,
  BodyType,
  ResponseType,
  ModifyType = ResponseType,
>(
  request: GRPCRequestAction<RequestType, BodyType, ResponseType>,
  successType: SuccessType,
  failureType: FailureType,
  config?: {
    /**
     * @deprecated Storing modified data specific to one caller in the cache is an anti-pattern.
     * We'd rather cache the raw response data and let multiple callers supply their own projections.
     *
     * Prefer using selectors or other methods to modify the raw data.
     *
     * We'd like to phase this out eventually as we move over to react-query.
     */
    modify?: (data: ResponseType) => ModifyType;
    cacheKey?: (url: string) => string;
    uid?: UID;
  },
): RequestLifecycleAction<
  RequestType,
  SuccessType,
  FailureType,
  BodyType,
  ResponseType,
  ModifyType
>;

export function createGrpcAction<
  RequestType extends string,
  SuccessType extends string,
  FailureType extends string,
  BodyType,
  ResponseType,
  ModifyType = ResponseType,
>(
  request: GRPCRequestAction<RequestType, BodyType, ResponseType>,
  successType: SuccessType,
  failureType: FailureType,
  config: {
    /**
     * @deprecated Storing modified data specific to one caller in the cache is an anti-pattern.
     * We'd rather cache the raw response data and let multiple callers supply their own projections.
     *
     * Prefer using selectors or other methods to modify the raw data.
     *
     * We'd like to phase this out eventually as we move over to react-query.
     */
    modify?: (data: ResponseType) => ModifyType;
    cacheKey?: (url: string) => string;
    uid?: UID;
  } = {},
) {
  const translatedRequest = request as unknown as RequestAction<RequestType, BodyType>;
  return createRequestAction(config)(translatedRequest, successType, failureType);
}

/**
 * Used instead of `createAction` inside of an action creator. The first type argument should be
 * passed explicitly so that the `data` field of the success action is properly typed.
 *
 * @param config (optional)
 * @param config.modify (optional) - a sync transformation function from D -> M, used to modify the request before
 *               the action is dispatched.
 * @param config.cacheKey (optional) - This function returns the string used as the key in the cache. All requests going through
 *                                     createRequestAction are cached based on a default combination of the url and a hash of the
 *                                     payload body (see sendRequestAction). However, there are times when a customized cache key
 *                                     may be needed to control the number of entries going into the cache.
 * @param config.uid (optional) - If included, this will be the UID that can be used to cancel this individual request.
 *                                See createRequestCancellationAction.
 * @param request - contains all of the information required to send an HTTP request
 * @param successType - a string literal action type for the action fired on success
 * @param failureType - a string literal action type for the action fired on failure
 *
 * @generic D: This is the incoming shape of the data from the server.
 * @generic M: If the modify param is passed, this is the shape of the modified data that's dispatched with the success action.
 * @generic R: The action type that's dispatched with the *started* event.
 * @generic S: The action type that's dispatched with the *successful* request event.
 * @generic F: The action type that's dispatched with the *failed* request event.
 * @generic B: The type that is passed as the request body. This type will also be attached to the dispatched response actions.
 */
export function createRequestAction<D>(config?: {
  cacheKey?: (url: string) => string;
  uid?: UID;
}): <R extends string, S extends string, F extends string, B = undefined>(
  request: RequestAction<R, B>,
  successType: S,
  failureType: F,
) => RequestLifecycleAction<R, S, F, B, D>;

// Overload #2: Complete customization: modified data and an optional customized cache key
export function createRequestAction<D, M>(config: {
  /**
   * @deprecated Storing modified data specific to one caller in the cache is an anti-pattern.
   * We'd rather cache the raw response data and let multiple callers supply their own projections.
   *
   * Prefer using selectors or other methods to modify the raw data.
   *
   * We'd like to phase this out eventually as we move over to react-query.
   */
  modify: (d: D) => M;
  cacheKey?: (url: string) => string;
  uid?: UID;
}): <R extends string, S extends string, F extends string, B = undefined>(
  request: RequestAction<R, B>,
  successType: S,
  failureType: F,
) => RequestLifecycleAction<R, S, F, B, D, M>;

export function createRequestAction<D, M = D>(
  config: {
    /**
     * @deprecated Storing modified data specific to one caller in the cache is an anti-pattern.
     * We'd rather cache the raw response data and let multiple callers supply their own projections.
     *
     * Prefer using selectors or other methods to modify the raw data.
     *
     * We'd like to phase this out eventually as we move over to react-query.
     */
    modify?: (d: D) => M;
    cacheKey?: (url: string) => string;
    uid?: UID;
  } = {},
) {
  return <R extends string, S extends string, F extends string, B>(
    request: RequestAction<R, B>,
    successType: S,
    failureType: F,
  ): RequestLifecycleAction<R, S, F, B, D, M> =>
    createAction({
      modify: config.modify,
      cacheKey: config.cacheKey,
      type: RequestActionType,
      request,
      successType,
      failureType,
      id: config.uid ?? UID(),
    });
}

export const createRequestQueueAction = (
  queueType: typeof AbortType | typeof CancelPendingType,
  kind: string,
  excludeTags?: string[],
): RequestQueueAction => {
  if (!kind) {
    console.warn('Warning! No request cancellation kind found, refusing to cancel the request');
  }
  return createAction({
    type: RequestActionQueueType,
    queueType,
    kind,
    excludeTags,
  });
};

/**
 * @deprecated please use {@link createGrpcAction} instead because it supports better type inference.
 *
 * The parallel to createRequestAction, but for gRPC endpoints. These are required to be used with
 * functions that match the GRPCFunction signature, so all the request/response types can be inferred from
 * the contents of that function. See the helper types for more details.
 *
 * @param config (optional)
 * @param config.modify (optional) - a sync transformation function from a gRPC endpoint's response type to a new type M,
 *                                  used to modify the request before the action is dispatched.
 * @param config.cacheKey (optional) - This function returns the string used as the key in the cache. All requests going through
 *                                     createGRPCAction/createRequestAction are cached based on a default combination of the url
 *                                     and a hash of the payload body (see sendRequestAction). However, there are times when a
 *                                     customized cache key may be needed to control the number of entries going into the cache.
 * @param request - contains all of the information required to send a gRPC request, see GRPCRequestAction for more details
 * @param successType - a string literal action type for the action fired on success
 * @param failureType - a string literal action type for the action fired on failure
 *
 * @generic G: This is the typeof of the current gRPC auto-generated function.
 * @generic M: If the modify param is passed, this is the shape of the modified data that's dispatched with the success action.
 * @generic R: The action type that's dispatched with the *started* event.
 * @generic S: The action type that's dispatched with the *successful* request event.
 * @generic F: The action type that's dispatched with the *failed* request event.
 */
export function createGRPCAction<G extends GRPCFunction<any, any>>(config?: {
  cacheKey?: (url: string) => string;
  uid?: UID;
}): <R extends string, S extends string, F extends string>(
  request: GRPCRequestAction<R, GRPCBodyType<G>, GRPCResponseType<G>>,
  successType: S,
  failureType: F,
) => RequestLifecycleAction<R, S, F, GRPCBodyType<G>, GRPCResponseType<G>, GRPCResponseType<G>>;

// Overload #2: Complete customization: modified data and an optional customized cache key
export function createGRPCAction<G extends GRPCFunction<any, any>, M>(config: {
  /**
   * @deprecated Storing modified data specific to one caller in the cache is an anti-pattern.
   * We'd rather cache the raw response data and let multiple callers supply their own projections.
   *
   * Prefer using selectors or other methods to modify the raw data.
   *
   * We'd like to phase this out eventually as we move over to react-query.
   */
  modify: (d: GRPCResponseType<G>) => M;
  cacheKey?: (url: string) => string;
  uid?: UID;
}): <R extends string, S extends string, F extends string>(
  request: GRPCRequestAction<R, GRPCBodyType<G>, GRPCResponseType<G>>,
  successType: S,
  failureType: F,
) => RequestLifecycleAction<R, S, F, GRPCBodyType<G>, GRPCResponseType<G>, M>;

export function createGRPCAction<G extends GRPCFunction<any, any>, M = GRPCResponseType<G>>(
  config: {
    /**
     * @deprecated Storing modified data specific to one caller in the cache is an anti-pattern.
     * We'd rather cache the raw response data and let multiple callers supply their own projections.
     *
     * Prefer using selectors or other methods to modify the raw data.
     *
     * We'd like to phase this out eventually as we move over to react-query.
     */
    modify?: (d: GRPCResponseType<G>) => M;
    cacheKey?: (url: string) => string;
    uid?: UID;
  } = {},
) {
  return <R extends string, S extends string, F extends string>(
    request: GRPCRequestAction<R, GRPCBodyType<G>, GRPCResponseType<G>>,
    successType: S,
    failureType: F,
  ): RequestLifecycleAction<R, S, F, GRPCBodyType<G>, GRPCResponseType<G>, M> => {
    const translatedRequest = request as unknown as RequestAction<R, GRPCBodyType<G>>;
    return createRequestAction<GRPCResponseType<G>, M>({
      modify: config.modify ? config.modify : (d: GRPCResponseType<G>) => d,
      cacheKey: config.cacheKey,
      uid: config.uid,
    })(translatedRequest, successType, failureType);
  };
}

export type createGRPCAction = (
  ...req: any[]
) => RequestLifecycleAction<any, any, any, any, any, any>;
export type createRequestAction = (
  ...req: any[]
) => RequestLifecycleAction<any, any, any, any, any, any>;

/**
 * Every request action will return a UID. Dispatching a request cancellation action with that
 * UID will abort that single request.
 * Firing a requestCancellationAction after a request has completed is a no-op, and is safe to do.
 * @param id - the UID used to cancel a request
 */
export const createRequestCancellationAction = (id: string): RequestCancellationAction =>
  createAction({
    type: RequestCancellationActionType,
    id,
  });
